import { Injectable } from "@angular/core";
import { FormArray, FormGroup, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { CURRENCY_DECIMAL, PERCENTAGE_DECIMAL, revertFormat } from "src/app/constants/global";
import { CostCenterDistribution, CostCenterDistributionCustomColumns } from "src/app/models/cost-center-distribution";
import { ModelValidators } from "src/app/models/validators/validators";

@Injectable()
export class CostCenterDistributionHelper {

  constructor(private translateService: TranslateService, private formBuilder: UntypedFormBuilder){ }
  
  //BuildForm
  Create(ccd: CostCenterDistribution, disabled, customColumns: CostCenterDistributionCustomColumns[]): UntypedFormGroup {
    let control = this.formBuilder.group({
      'ID': [ccd?.ID || 0, ''],
      'CostCenterID': [{ value: ccd?.CostCenterID || null, disabled: disabled }, Validators.compose([Validators.required])],
      'CostCenterName': [{ value: null, disabled: disabled }],
      'Percentage': [{ value: (ccd?.Percentage || 0).formatDecimal(this.translateService.currentLang, (+PERCENTAGE_DECIMAL)), disabled:  disabled }],
      'Value': [{ value: (ccd?.Value || 0).formatDecimal(this.translateService.currentLang, (+CURRENCY_DECIMAL)), disabled: disabled }, Validators.compose([ModelValidators.numberVal({ min: 0, decimalPlaces: +CURRENCY_DECIMAL })])],
    })
  
    return this.AddCustomColumns(control, ccd, disabled, customColumns);
  }

  //Adiciona campos adicionais
  AddCustomColumns(control: UntypedFormGroup, e?:CostCenterDistribution, disabled: boolean = false, customColumns: CostCenterDistributionCustomColumns[] = null){

    customColumns?.forEach((x:CostCenterDistributionCustomColumns) => {
      control.addControl(x.name, new UntypedFormControl({ value: e?.[x.name] ?? null, disabled: disabled }, x.isRequired ? Validators.compose([Validators.required]) : {}))
    })

    return control;
  }

  updateValuesBasedOnPercentages(form: FormArray, totalNetAmount: number) {

    if (!form || !form.controls || !(form.controls.length > 0))
      return;
  
    let totalAssignedValue = 0;
    let totalAssignedPercentage = 0;
    let totalPercentage = 100;
  
    const numItems = form.length;
  
    // Recalcula os valores para cada item, garantindo que o último item ajuste a diferença
    form.controls.forEach((x: FormGroup, index: number) => {
      const isLastItem = index === numItems - 1;
      const percentage = x.controls["Percentage"].value.toString().revertDecimal() || "0";
  
      // Calcula o valor com base na porcentagem para todos os itens, exceto o último
      let value = isLastItem
        ? totalNetAmount - totalAssignedValue // Ajusta o valor do último item para garantir que o total seja o esperado
        : (totalNetAmount * percentage) / 100;
  
      value = value.formatDecimal(this.translateService.currentLang, (+CURRENCY_DECIMAL)).revertDecimal();

      // Acumula o valor atribuído
      totalAssignedValue += value;
  
      // Se o último item, ajusta a porcentagem com o valor restante
      if (isLastItem) {
        value = totalNetAmount - totalAssignedValue + value; // Corrige o valor final se houver diferença
      }
  
      // Atualiza o controle de valor do formulário
      x.controls["Value"].setValue(value.formatDecimal(this.translateService.currentLang, (+CURRENCY_DECIMAL)));
  
      
    });
  
    // Opcional: Atualiza a validade do formulário
    form.updateValueAndValidity();
  }

  updateValuesBasedOnValues(form: FormArray, totalNetAmount: number) {

    if (!form || !form.controls || !(form.controls.length > 0))
      return;
  
    let totalAssignedValue = 0;
    let totalAssignedPercentage = 0;
  
    const numItems = form.length;
  
    // Recalcula os valores para cada item, garantindo que o último item ajuste a diferença
    form.controls.forEach((x: FormGroup, index: number) => {
      const isLastItem = index === numItems - 1;
      const value = x.controls["Value"].value.toString().revertDecimal() || "0";
  
      // Acumula o valor atribuído
      totalAssignedValue += value;
  
      // Se for o último item, ajusta o valor para garantir que o total seja igual ao totalNetAmount
      let adjustedValue = isLastItem
        ? totalNetAmount - totalAssignedValue + value // Ajuste devido a arredondamento
        : value;
  
      // Calcula a porcentagem com base no valor
      let percentage = (adjustedValue * 100) / totalNetAmount;
      percentage = percentage.formatDecimal(this.translateService.currentLang, (+PERCENTAGE_DECIMAL)).revertDecimal();
      
      // Se for o último item, ajusta a porcentagem para garantir que a soma seja 100%
      if (isLastItem) {
        percentage = 100-totalAssignedPercentage;
      }
  
      totalAssignedPercentage += percentage;

      // Atualiza a porcentagem no formulário
      x.controls["Percentage"].setValue(percentage.formatDecimal(this.translateService.currentLang, (+PERCENTAGE_DECIMAL)));
  
    });
  
    // Opcional: Atualiza a validade do formulário
    form.updateValueAndValidity();
  }

//Contratos ngModel
  updateValuesBasedOnPercentagesCCD(ccd: CostCenterDistribution[], totalNetAmount: number) {

    if (!ccd || !(ccd.length > 0))
      return;

    let totalAssignedValue = 0;
    const numItems = ccd.length;

    // Recalcula os valores para cada item, garantindo que o último item ajuste a diferença
    ccd.forEach((x: CostCenterDistribution, index: number) => {
        const isLastItem = index === numItems - 1;

        // Calcula o valor com base na percentagem
        let value = isLastItem
            ? totalNetAmount - totalAssignedValue // Ajusta o valor do último item
            : (totalNetAmount * x.Percentage) / 100;

        // Arredondamento do valor
        value = parseFloat(value.toFixed(2));

        // Acumula o valor atribuído
        totalAssignedValue += value;

        // Atualiza o controle do formulário
        x.Value = value;
    });
  }
}

